import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'info' | 'warning';
type ToastSeverity = 'success' | 'warning' | 'critical';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  action?: ToastAction;
  duration?: number;
  sticky?: boolean; // no auto-dismiss — manual close only
  severity?: ToastSeverity; // for logging: success | warning | critical
  dealId?: string; // scoped to deal for /logs filtering
}

type ToastListener = (toast: Toast) => void;

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  onToast: (listener: ToastListener) => () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

export type { Toast, ToastSeverity };

// ─── Icons ────────────────────────────────────────────────────────────────────

const iconMap: Record<ToastType, ReactNode> = {
  success: (
    <svg className="w-5 h-5 text-[var(--brand-success)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5 text-[var(--brand-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5 text-[var(--brand-warning)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3l9.66 16.5H2.34L12 3z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5 text-[var(--brand-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
      <circle cx="12" cy="12" r="10" strokeWidth={2} />
    </svg>
  ),
};

// ─── Title Renderer (detects [$XXK] amount badges) ───────────────────────────

function renderTitle(title: string): ReactNode {
  const match = title.match(/^(.*?)(\[\$[\d.,]+[KMB]?\])(.*)$/i);
  if (!match) return title;
  return (
    <>
      {match[1]}
      <span className="inline-block ml-1 px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: '#EAF3DE', color: '#3B6D11' }}>
        {match[2]}
      </span>
      {match[3]}
    </>
  );
}

// ─── Single Toast ─────────────────────────────────────────────────────────────

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    // Enter animation
    requestAnimationFrame(() => setVisible(true));

    const duration = toast.sticky ? 0 : (toast.duration ?? 5000);
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onDismiss(toast.id), 200);
      }, duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      role="alert"
      className={[
        'flex items-start gap-3 w-full max-w-sm p-4',
        'rounded-[var(--radius-lg)]',
        'border border-[var(--glass-border)]',
        'bg-[var(--glass-bg-heavy)] backdrop-blur-[var(--glass-blur)]',
        'shadow-[var(--glass-shadow)]',
        'transition-all duration-200',
        visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-2',
      ].join(' ')}
      style={toast.sticky ? { borderLeft: '3px solid var(--brand-danger)', animation: 'pulse 2s ease-in-out infinite' } : undefined}
    >
      <div className="shrink-0 mt-0.5">{iconMap[toast.type]}</div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          {renderTitle(toast.title)}
        </p>
        {toast.description && (
          <p className="mt-0.5 text-xs text-[var(--text-muted)]">
            {toast.description}
          </p>
        )}
        {toast.action && (
          <button type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            className="mt-2 text-xs font-medium text-[var(--brand-primary)] hover:underline min-h-[44px] min-w-[44px] inline-flex items-center"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button type="button"
        onClick={() => {
          setVisible(false);
          setTimeout(() => onDismiss(toast.id), 200);
        }}
        className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2 -mt-2 rounded-[var(--radius-full)] hover:bg-[var(--bg-hover)] transition-colors"
        aria-label="Dismiss notification"
      >
        <svg className="w-4 h-4 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const listenersRef = useRef<Set<ToastListener>>(new Set());

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${++toastCounter}`;
    const full = { ...toast, id };
    setToasts((prev) => [...prev, full]);
    // Notify listeners (for logging)
    listenersRef.current.forEach((fn) => { try { fn(full) } catch {} });
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const onToast = useCallback((listener: ToastListener) => {
    listenersRef.current.add(listener);
    return () => { listenersRef.current.delete(listener); };
  }, []);

  // Memoize the context value — an inline object literal here flips the context
  // ref every render, forcing every useToast consumer to re-render (and, paired
  // with a [toast]-deps consumer effect, a re-render storm). See Lab Notes 2026-05-25.
  const ctxValue = useMemo(() => ({ addToast, removeToast, onToast }), [addToast, removeToast, onToast]);

  return (
    <ToastContext.Provider value={ctxValue}>
      {children}
      {createPortal(
        <div
          className="fixed bottom-4 right-4 z-[60] flex flex-col-reverse gap-2 pointer-events-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto">
              <ToastItem toast={toast} onDismiss={removeToast} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
